import { inject, Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpEventType } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators'; // Import finalize for cleaning up
import { LoaderService } from '../../components/loader/loader.service';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { AuthContext } from '../services/auth.context';
import { BannerAlertService } from '../../components/banner-alert/banner-alert.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
    providedIn: 'root'
})
export class HttpInterceptorService implements HttpInterceptor {

    private snackBar = inject(MatSnackBar);

    constructor(
        private authContext: AuthContext,
        private loaderService: LoaderService,
        private router: Router, // Inject Router,
        private bannerAlertService: BannerAlertService,
    ) { }  // Inject LoaderService

    private readonly baseUrl = environment.apiUrl;  // Your base URL

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        // Get the Bearer token from the auth service
        const token = this.authContext.getToken();

        // Check if the request URL is relative and prepend the base URL if needed
        const apiReq = req.url.startsWith('http')
            ? req  // If the URL is absolute, leave it as is
            : req.clone({ url: `${this.baseUrl}${req.url}` });  // Prepend base URL for relative paths

        // Clone the request to add the Bearer token to the headers
        const authReq = apiReq.clone({
            setHeaders: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`, // Add Authorization header with Bearer token
            }
        });

        // Show the loader
        this.loaderService.show();

        console.log('Intercepted and modified request:');

        // Forward the modified request and hide the loader when done
        return next.handle(authReq).pipe(
            map((event: HttpEvent<any>) => {
                console.log('Response received: event');
                // Process the response if needed
                if (event.type === HttpEventType.Response) {
                    // You can log or modify the response here
                    console.log('Response received: event.body');
                }
                return event; // Return the event as is, or modify it if necessary
            }),
            catchError((error) => {
                // Handle the error appropriately
                console.error('Error occurred:', error);
                // Optionally, show a user-friendly message
                // this.notificationService.showError('An error occurred. Please try again.');

                // Check if the error is due to unauthorized access (e.g., status code 401)
                if (error.status === 401) {
                    localStorage.clear();
                    this.router.navigate(['/app/login']); // Redirect to login page
                } else if (error.status === 500) {
                    localStorage.clear();
                    this.router.navigate(['/app/login']); // Redirect to login page
                } else if (error.status === 400) {
                    if (error.error instanceof Object) {
                        const errorKeys = Object.keys(error.error || {}).filter(key => error[key] != null); // Filters out both null and undefined
                        const errorMessages = errorKeys.map(key => error.error[key]);
                        this.bannerAlertService.sharingData.next({ errorKeys, errorMessages });
                    } else {
                        localStorage.clear();
                        this.router.navigate(['/app/login']);
                    }
                } else if (error.status === 422) {
                    console.log(error.error);
                    const errorKeys = Object.keys(error.error || {}).filter(key => error[key] != null); // Filters out both null and undefined
                    const errorMessages = errorKeys.map(key => error.error[key]);
                    this.bannerAlertService.sharingData.next({ errorKeys, errorMessages });
                } else if (error.status === 429) {
                    this.snackBar.open(error.error, '', { duration: 3 * 1000 }); // 3 seconds
                } else {
                    localStorage.clear();
                    this.router.navigate(['/app/login']);
                }

                return throwError(() => error); // Updated usage of throwError
            }),
            finalize(() => {
                // Hide the loader after the request is complete
                this.loaderService.hide();
            })
        );
    }
}
